import type { Processor, Program } from '../types/domain';

/** Processor row with `processorType === 'BLOCKCHAIN'` (case-insensitive). */
export function getBlockchainProcessorId(
  processors: Processor[] | null | undefined,
): number | null {
  if (!processors?.length) return null;
  const p = processors.find(
    (x) => String(x.processorType).toUpperCase() === 'BLOCKCHAIN',
  );
  return p != null ? p.processorId : null;
}

/**
 * Program's selected processor id (admin stores this as `processorType` on the program).
 */
export function getProgramProcessorTypeId(program: unknown): number | null {
  if (!program || typeof program !== 'object') return null;
  const o = program as Record<string, unknown>;
  const raw =
    o.processorType ??
    o.processorId ??
    o['processor.processorId'];
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function isWalletAddressCollection(
  program: unknown,
  processors: Processor[] | null | undefined,
): boolean {
  const blockchainId = getBlockchainProcessorId(processors);
  const programProcId = getProgramProcessorTypeId(program);
  if (blockchainId == null || programProcId == null) return false;
  return programProcId === blockchainId;
}

/** Merge list row with GET /programs/:id payload so processor fields are available early. */
export function resolveProgramForCollection(
  selectedProgramId: number | null,
  programFromRedux: unknown,
  programListRows: Program[],
): unknown {
  if (selectedProgramId == null) return programFromRedux;
  const detail =
    programFromRedux &&
    typeof programFromRedux === 'object' &&
    (programFromRedux as Program).programId === selectedProgramId
      ? programFromRedux
      : null;
  const row = programListRows.find((p) => p.programId === selectedProgramId);
  if (detail && row) return { ...row, ...detail };
  return detail ?? row ?? programFromRedux;
}

const ETH_ADDRESS = /^0x[a-fA-F0-9]{40}$/;

/** Parse a wallet address from manual entry or full QR string. */
export function normalizeWalletAddress(input: string): string | null {
  const s = input.trim();
  if (!s) return null;
  if (ETH_ADDRESS.test(s)) return s;
  const embedded = s.match(/0x[a-fA-F0-9]{40}/);
  if (embedded) return embedded[0];
  if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(s)) return s;
  if (s.length >= 26 && s.length <= 100 && /^[a-zA-Z0-9]+$/.test(s)) return s;
  return null;
}
